import type { CheckoutRequest } from "./shopify";

const checkoutElementId = "tunetailor-shopify-checkout";

interface CheckoutKitElement extends EventTarget {
  src: string;
  target: string;
  open(): void;
  close(): void;
  remove(): void;
}

export interface CheckoutPresentationCallbacks {
  onClose?: () => void;
  onComplete?: () => void;
  onError?: () => void;
}

export interface CheckoutKitRuntime {
  create(): Promise<CheckoutKitElement>;
  redirect(url: string): void;
}

const browserRuntime: CheckoutKitRuntime = {
  async create() {
    await import("@shopify/checkout-kit");

    const existing = document.getElementById(checkoutElementId) as CheckoutKitElement | null;
    existing?.close();
    existing?.remove();

    const checkout = document.createElement("shopify-checkout") as CheckoutKitElement & HTMLElement;
    checkout.id = checkoutElementId;
    document.body.appendChild(checkout);
    return checkout;
  },
  redirect(url) {
    window.location.assign(url);
  },
};

export async function submitCheckoutRequest(
  request: CheckoutRequest,
  callbacks: CheckoutPresentationCallbacks = {},
): Promise<"checkout-kit" | "redirect" | "form"> {
  if (request.type === "redirect") {
    return presentCheckoutUrl(request.url, callbacks);
  }

  await fetch(request.clearCartUrl, {
    method: "POST",
    mode: "no-cors",
    credentials: "include",
    keepalive: true,
  });

  const form = document.createElement("form");
  form.method = "POST";
  form.action = request.action;
  form.style.display = "none";

  for (const field of request.fields) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = field.name;
    input.value = field.value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  return "form";
}

export async function presentCheckoutUrl(
  url: string,
  callbacks: CheckoutPresentationCallbacks = {},
  runtime: CheckoutKitRuntime = browserRuntime,
): Promise<"checkout-kit" | "redirect"> {
  assertSecureCheckoutUrl(url);

  let checkout: CheckoutKitElement;
  try {
    checkout = await runtime.create();
  } catch {
    runtime.redirect(url);
    return "redirect";
  }

  let settled = false;
  const finish = (callback: (() => void) | undefined) => {
    if (settled) {
      return;
    }

    settled = true;
    try {
      callback?.();
    } finally {
      checkout.close();
      checkout.remove();
    }
  };

  checkout.addEventListener("checkout:complete", () => finish(callbacks.onComplete), { once: true });
  checkout.addEventListener("checkout:error", () => finish(callbacks.onError), { once: true });
  checkout.addEventListener(
    "checkout:close",
    () => {
      if (!settled) {
        settled = true;
        callbacks.onClose?.();
      }
      checkout.remove();
    },
    { once: true },
  );

  checkout.src = url;
  checkout.target = "popup";

  try {
    checkout.open();
    return "checkout-kit";
  } catch {
    checkout.remove();
    runtime.redirect(url);
    return "redirect";
  }
}

function assertSecureCheckoutUrl(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("Shopify returned an invalid checkout URL.");
  }

  if (parsed.protocol !== "https:") {
    throw new Error("Shopify checkout must use HTTPS.");
  }
}
